import { useEffect, useCallback, useRef, useState } from 'react';
import { Controls } from './Controls';
import { Scrubber } from './Scrubber';
import { PlaybackSpeed } from './PlaybackSpeed';
import { VolumeControl } from './VolumeControl';
import { SleepTimer } from './SleepTimer';
import { ErrorDisplay } from './ErrorDisplay';
import { NowPlaying } from '../Live';
import { DEFAULT_STATION_IMAGE } from '../../constants';
import {
  useAudioPlayer,
  useMediaSession,
  updateMediaSessionPosition,
  useKeyboardShortcuts,
  usePlaybackPosition,
  useSleepTimer,
} from '../../hooks';
import { parseEpisodeTitle, formatDateTime } from '../../utils/time';
import { HiShare, HiRss, HiInformationCircle, HiQueueList, HiListBullet, HiCalendarDays } from 'react-icons/hi2';
import type { Episode, Stream } from '../../types';

interface PlayerProps {
  episode: Episode;
  stream: Stream;
  presenter?: string;
  initialTime?: number;
  endTime?: number; // Stop playback at this time (seconds) — for highlight/clip mode
  titleOverride?: string; // Custom title for highlight/clip mode
  imageOverride?: string; // Custom image URL for highlight/clip mode
  isLive?: boolean;
  autoplay?: boolean;
  onSearchFocus?: () => void;
  onPrevTrack?: () => void;
  onNextTrack?: () => void;
  hasPrevTrack?: boolean;
  hasNextTrack?: boolean;
  onEnded?: () => void;
  onShare?: () => void;
  onSubscribe?: () => void;
  onInfo?: () => void;
  showSubscribe?: boolean;
  onOpenQueue?: () => void;
  onOpenEpisodes?: () => void;
  queueCount?: number;
  onShowInfo?: (info: { title: string; description?: string; image?: string }) => void;
  onImageChange?: (image: string | null) => void;
}

export function Player({
  episode,
  stream,
  presenter,
  initialTime,
  endTime,
  titleOverride,
  imageOverride,
  isLive = false,
  autoplay = false,
  onSearchFocus,
  onPrevTrack,
  onNextTrack,
  hasPrevTrack = false,
  hasNextTrack = false,
  onEnded,
  onShare,
  onSubscribe,
  onInfo,
  showSubscribe = true,
  onOpenQueue,
  onOpenEpisodes,
  queueCount = 0,
  onShowInfo,
  onImageChange,
}: PlayerProps) {
  const audioSrc = isLive ? (stream.live_url || stream.url || '') : episode.fileurl || '';

  const {
    isPlaying,
    isLoading,
    isBuffering,
    isStalled,
    currentTime,
    duration,
    progress,
    playbackRate,
    volume,
    muted,
    error,
    toggle,
    seekRelative,
    seekToPercent,
    setPlaybackRate,
    setVolume,
    toggleMute,
    retry,
    audioRef,
    play,
    pause,
  } = useAudioPlayer(audioSrc, initialTime, onEnded);

  // ── Clip mode (highlight/timestamp linking) ──────────────────────────
  const isClipMode = endTime !== undefined && initialTime !== undefined;
  const clipStart = isClipMode ? initialTime! : 0;
  const clipDuration = isClipMode ? endTime! - clipStart : duration;
  const clipCurrentTime = isClipMode ? Math.max(0, currentTime - clipStart) : currentTime;
  const clipProgress = clipDuration > 0 ? (clipCurrentTime / clipDuration) * 100 : progress;

  // End time enforcement: stop playback when reaching the clip boundary
  useEffect(() => {
    if (endTime !== undefined && currentTime >= endTime && isPlaying) {
      pause();
      onEnded?.();
    }
  }, [currentTime, endTime, isPlaying, pause, onEnded]);

  // Save playback position for resume functionality
  usePlaybackPosition({
    episodeId: episode.episodeid,
    currentTime,
    duration,
    isPlaying,
    isLive,
  });

  // Sleep timer - pauses playback when timer expires
  const sleepTimer = useSleepTimer(pause);

  // Live mode: track when user is behind the live edge (e.g. after pausing)
  const [behindLive, setBehindLive] = useState(false);

  const jumpToLive = useCallback(() => {
    if (audioRef.current) {
      const src = audioRef.current.src;
      audioRef.current.src = src;
      audioRef.current.load();
      audioRef.current.play();
      setBehindLive(false);
    }
  }, [audioRef]);

  // Live show image override (set by NowPlaying when a show with artwork is on air)
  const [liveImage, setLiveImage] = useState<string | null>(null);

  // Handle the NowPlaying image callback: update local state + notify parent
  const handleImageChange = useCallback((image: string | null) => {
    setLiveImage(image);
    onImageChange?.(image);
  }, [onImageChange]);

  // Content transition state for smooth episode changes
  const [contentVisible, setContentVisible] = useState(true);
  const [displayedImage, setDisplayedImage] = useState(imageOverride || episode.episodeimage || episode.image || stream.image);
  const [imageLoaded, setImageLoaded] = useState(false);
  const prevEpisodeIdRef = useRef(episode.episodeid);

  // Handle smooth content transitions when episode changes
  useEffect(() => {
    if (prevEpisodeIdRef.current !== episode.episodeid) {
      // Episode changed - fade out, update, fade in
      setContentVisible(false);
      setImageLoaded(false);

      const timer = setTimeout(() => {
        setDisplayedImage(imageOverride || episode.episodeimage || episode.image || stream.image);
        prevEpisodeIdRef.current = episode.episodeid;
        // Small delay before fading in to allow image to start loading
        setTimeout(() => setContentVisible(true), 50);
      }, 200); // Match fade-out duration

      return () => clearTimeout(timer);
    }
  }, [episode.episodeid, episode.episodeimage, episode.image, stream.image]);

  // Update album art when the live show image changes
  useEffect(() => {
    if (!isLive) return;
    const newImage = liveImage || episode.image || stream.image;
    if (newImage !== displayedImage) {
      setImageLoaded(false);
      setDisplayedImage(newImage);
    }
  }, [isLive, liveImage, episode.image, stream.image, displayedImage]);

  // Auto-start playback when autoplay flag is set (only once per episode)
  const hasAutoplayedRef = useRef(false);
  useEffect(() => {
    // Reset autoplay flag when episode changes
    hasAutoplayedRef.current = false;
  }, [episode.episodeid]);

  useEffect(() => {
    if (autoplay && !isLoading && !hasAutoplayedRef.current) {
      hasAutoplayedRef.current = true;
      play();
    }
  }, [autoplay, isLoading, play]);

  // Parse episode title — use override in clip/highlight mode
  const { name: parsedName, datetime: episodeDatetime } = parseEpisodeTitle(
    episode.episodetitle
  );
  const episodeName = titleOverride || parsedName;

  // Format the datetime (hide in clip mode — the clip has its own context)
  const formattedDatetime = titleOverride ? '' : (episodeDatetime
    ? formatDateTime(episodeDatetime, stream.time_string_format)
    : '');

  // Get the image to display (prefer live show image when on air)
  const imageUrl = imageOverride || (isLive && liveImage) || episode.episodeimage || episode.image || stream.image;

  // Media Session integration (no seek controls in live mode)
  useMediaSession({
    title: episodeName,
    artist: presenter || stream.streamname,
    album: formattedDatetime,
    artwork: imageUrl,
    onPlay: play,
    onPause: pause,
    onSeekForward: isLive ? undefined : () => seekRelative(30),
    onSeekBackward: isLive ? undefined : () => seekRelative(-30),
    onPrevTrack: onPrevTrack,
    onNextTrack: onNextTrack,
  });

  // Update media session position
  useEffect(() => {
    updateMediaSessionPosition(currentTime, duration, playbackRate);
  }, [currentTime, duration, playbackRate]);

  // Speed control handlers
  const handleSpeedUp = useCallback(() => {
    const newRate = Math.min(2, playbackRate + 0.05);
    setPlaybackRate(Math.round(newRate * 100) / 100);
  }, [playbackRate, setPlaybackRate]);

  const handleSpeedDown = useCallback(() => {
    const newRate = Math.max(0.5, playbackRate - 0.05);
    setPlaybackRate(Math.round(newRate * 100) / 100);
  }, [playbackRate, setPlaybackRate]);

  // Keyboard shortcuts (seek disabled in live mode)
  const noop = useCallback(() => {}, []);
  useKeyboardShortcuts({
    onPlayPause: toggle,
    onSeekForward: isLive ? noop : () => seekRelative(30),
    onSeekBackward: isLive ? noop : () => seekRelative(-30),
    onSpeedUp: isLive ? noop : handleSpeedUp,
    onSpeedDown: isLive ? noop : handleSpeedDown,
    onPrevTrack: onPrevTrack,
    onNextTrack: onNextTrack,
    onFocusSearch: onSearchFocus,
  });

  // Set theme colors from stream settings
  const brandColor = stream.player_color || stream.color || '#6366f1';
  const accentColor = stream.accent_color || brandColor;
  useEffect(() => {
    document.documentElement.style.setProperty('--color-primary', accentColor);
    document.documentElement.style.setProperty('--color-brand', brandColor);
  }, [brandColor, accentColor]);

  return (
    <div className="flex flex-col items-center w-full h-full relative overflow-hidden">
      {/* Immersive blurred background - uses album art */}
      <div className="absolute inset-0 z-0">
        {/* Blurred album art background */}
        <img
          src={imageUrl}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover scale-110 blur-3xl opacity-60"
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            if (target.src !== stream.image && stream.image) {
              target.src = stream.image;
            } else if (!target.src.endsWith(DEFAULT_STATION_IMAGE)) {
              target.src = DEFAULT_STATION_IMAGE;
            }
          }}
        />
        {/* Tint wash */}
        <div
          className="absolute inset-0 opacity-15"
          style={{ backgroundColor: 'var(--color-tint)' }}
        />
        {/* Gradient overlays for depth */}
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(180deg,
              var(--color-bg) 0%,
              transparent 30%,
              transparent 70%,
              var(--color-bg) 100%)`,
          }}
        />
        <div
          className="absolute inset-0 opacity-50"
          style={{
            background: `radial-gradient(ellipse at center top, var(--color-tint) 0%, transparent 60%)`,
          }}
        />
      </div>

      {/* Album art - hero element floating in the immersive space */}
      {/* Uses @container queries to adapt based on available height */}
      <div className="album-art-container relative w-full flex-1 min-h-0 flex items-center justify-center z-10"
           data-image-style={stream.image_style || 'bordered'}>
        {/* Container adapts based on available space:
            - Very small (<150px): Hidden completely
            - Small (150-280px): Full bleed only if image_style="full", otherwise scales down
            - Normal (>280px): Padded with rounded corners */}
        <div
          className={`album-art-wrapper relative w-full h-full
                     transition-all duration-300 ease-out
                     ${contentVisible && imageLoaded ? 'opacity-100' : 'opacity-0'}`}
        >
          {/* Image container - styling controlled by CSS container queries */}
          <div className="album-art-inner absolute inset-0 overflow-hidden">
            <img
              src={displayedImage}
              alt={`${stream.streamname} - ${episodeName}`}
              className="w-full h-full object-cover"
              onLoad={() => setImageLoaded(true)}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                if (target.src !== stream.image && stream.image) {
                  target.src = stream.image;
                } else if (!target.src.endsWith(DEFAULT_STATION_IMAGE)) {
                  target.src = DEFAULT_STATION_IMAGE;
                }
                setImageLoaded(true);
              }}
            />
          </div>

          {/* Live badge overlaid on image */}
          {isLive && (
            <div className="absolute top-3 right-3 z-10">
              <span className="inline-flex items-center px-3 py-1.5 text-xs font-bold
                             text-white bg-red-500 rounded-full shadow-lg
                             animate-pulse-slow backdrop-blur-sm">
                <span className="w-2 h-2 bg-white rounded-full mr-2 animate-pulse" />
                LIVE
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Info & Controls Card - edge to edge */}
      <div className="w-full flex-shrink-0 z-10">
        <div className="bg-[var(--color-surface)]/50 backdrop-blur-xl
                        border-t border-[var(--color-border)]/50 shadow-[var(--shadow-lg)]
                        overflow-hidden">

          {/* Episode Info - with sidebar buttons and transition */}
          <div className={`pt-5 transition-opacity duration-300
                          ${isLive && stream.show_now_playing === false ? 'pb-8' : 'pb-4'}
                          ${contentVisible ? 'opacity-100' : 'opacity-0'}`}>
            {/* Station badge */}
            {stream.show_station_badge !== false && (
              <div className="flex justify-center mb-2.5">
                <span
                  className="px-3 py-1 text-[11px] font-semibold text-white rounded-full"
                  style={{ backgroundColor: brandColor }}
                >
                  {stream.streamname}
                </span>
              </div>
            )}

            {/* Title row with sidebar buttons */}
            <div className="flex items-center gap-2 px-2">
              {/* Schedule button (live mode) or Queue button (on-demand) - left side */}
              {isLive ? (
                onOpenQueue && (
                  <button
                    onClick={onOpenQueue}
                    aria-label="View schedule"
                    className="lg:hidden flex-shrink-0 flex items-center justify-center w-10 h-10
                               text-[var(--color-text-muted)]
                               hover:text-[var(--color-text)]
                               hover:bg-[var(--color-surface-elevated)]/60
                               transition-colors rounded-full
                               focus:outline-none focus-visible:ring-2
                               focus-visible:ring-[var(--color-primary)]"
                  >
                    <HiCalendarDays size={20} />
                  </button>
                )
              ) : (
                onOpenQueue && (
                  <button
                    onClick={onOpenQueue}
                    aria-label="Open queue"
                    className="lg:hidden flex-shrink-0 flex items-center justify-center w-10 h-10
                               text-[var(--color-text-muted)]
                               hover:text-[var(--color-text)]
                               hover:bg-[var(--color-surface-elevated)]/60
                               transition-colors rounded-full
                               focus:outline-none focus-visible:ring-2
                               focus-visible:ring-[var(--color-primary)]
                               relative"
                  >
                    <HiQueueList size={20} />
                    {queueCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 w-4 h-4 flex items-center justify-center
                                       bg-[var(--color-primary)] text-white text-[9px] font-bold
                                       rounded-full">
                        {queueCount > 9 ? '9+' : queueCount}
                      </span>
                    )}
                  </button>
                )
              )}
              {/* Spacer for lg screens where queue/schedule button is hidden */}
              <div className="hidden lg:block w-10 flex-shrink-0" />

              {/* Episode title - centered, truncated */}
              <div className="flex-1 min-w-0 text-center">
                <h1 className="text-xl font-semibold text-[var(--color-text)]
                               tracking-tight leading-snug line-clamp-2 px-2">
                  {isLive ? 'Live Now' : episodeName}
                </h1>
              </div>

              {/* Episodes button - right side (hidden on md when sidebar visible) */}
              {onOpenEpisodes && (
                <button
                  onClick={onOpenEpisodes}
                  aria-label="Browse episodes"
                  className="md:hidden flex-shrink-0 flex items-center justify-center w-10 h-10
                             text-[var(--color-text-muted)]
                             hover:text-[var(--color-text)]
                             hover:bg-[var(--color-surface-elevated)]/60
                             transition-colors rounded-full
                             focus:outline-none focus-visible:ring-2
                             focus-visible:ring-[var(--color-primary)]"
                >
                  <HiListBullet size={20} />
                </button>
              )}
              {/* Spacer for md+ screens where episodes button is hidden */}
              <div className="hidden md:block w-10 flex-shrink-0" />
            </div>

            {/* Presenter and date (on-demand mode) */}
            {!isLive && (
              <div className="text-center mt-1.5 px-4">
                {presenter && (
                  <p className="text-sm text-[var(--color-text-secondary)]">
                    {presenter}
                  </p>
                )}
                {formattedDatetime && (
                  <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
                    {formattedDatetime}
                  </p>
                )}
              </div>
            )}

            {/* Now Playing info (live mode) */}
            {isLive && (stream.show_now_playing !== false || stream.show_icecast_track === true) && (
              <div className="mt-2 px-4">
                <NowPlaying streamId={stream.streamid} pollInterval={30000} onShowInfo={onShowInfo} onImageChange={handleImageChange} showNowPlaying={stream.show_now_playing !== false} showIcecastTrack={stream.show_icecast_track === true} />
              </div>
            )}
          </div>

          {/* Error display */}
          {error && (
            <ErrorDisplay error={error} onRetry={retry} isLive={isLive} />
          )}

          {/* Scrubber (not shown for live) */}
          {!isLive && (
            <div className="px-4 pb-2">
              <Scrubber
                currentTime={clipCurrentTime}
                duration={clipDuration}
                progress={clipProgress}
                onSeek={(percent) => {
                  // In clip mode, seeking maps to the clip range within the episode
                  const targetTime = clipStart + (percent / 100) * clipDuration;
                  seekToPercent((targetTime / duration) * 100);
                }}
                onSeekToTime={(seconds) => {
                  const targetTime = clipStart + seconds;
                  seekToPercent((targetTime / duration) * 100);
                }}
                disabled={isLoading}
                isTransitioning={!contentVisible || isLoading}
              />
            </div>
          )}

          {/* Playback Controls */}
          <div className="pb-6 -mt-6">
            <Controls
              isPlaying={isPlaying}
              isBuffering={isBuffering || isStalled}
              isLive={isLive}
              onPlayPause={isLive ? () => {
                toggle();
                if (isPlaying) setBehindLive(true);
              } : toggle}
              onForward={() => {
                if (isClipMode && endTime !== undefined) {
                  const target = Math.min(currentTime + 30, endTime);
                  seekToPercent((target / duration) * 100);
                } else {
                  seekRelative(30);
                }
              }}
              onBackward={() => {
                if (isClipMode) {
                  const target = Math.max(currentTime - 30, clipStart);
                  seekToPercent((target / duration) * 100);
                } else {
                  seekRelative(-30);
                }
              }}
              onForwardLarge={isClipMode ? undefined : () => seekRelative(300)}
              onBackwardLarge={isClipMode ? undefined : () => seekRelative(-300)}
              onPrevTrack={onPrevTrack}
              onNextTrack={onNextTrack}
              hasPrevTrack={hasPrevTrack}
              hasNextTrack={hasNextTrack}
              disabled={isLoading}
              isTransitioning={!contentVisible || isLoading}
              behindLive={behindLive}
              onJumpToLive={jumpToLive}
            />
          </div>

          {/* Secondary Controls */}
          {!isLive && (
            <div className="flex items-center justify-center gap-4 px-4 py-2.5
                            border-t border-[var(--color-border)]/30
                            bg-[var(--color-surface)]/60 backdrop-blur-xl">
              <VolumeControl
                volume={volume}
                muted={muted}
                onVolumeChange={setVolume}
                onMuteToggle={toggleMute}
              />
              <div className="w-px h-5 bg-[var(--color-border)]/50" />
              <PlaybackSpeed rate={playbackRate} onRateChange={setPlaybackRate} />
              <div className="w-px h-5 bg-[var(--color-border)]/50" />
              <SleepTimer
                isActive={sleepTimer.isActive}
                remainingFormatted={sleepTimer.remainingFormatted}
                onSetTimer={sleepTimer.setTimer}
                onCancel={sleepTimer.cancelTimer}
              />
              <div className="w-px h-5 bg-[var(--color-border)]/50" />
              {/* Episode action buttons */}
              <div className="flex items-center gap-1">
                {onShare && (
                  <button
                    onClick={onShare}
                    aria-label="Share this episode"
                    className="flex items-center justify-center w-8 h-8
                               text-[var(--color-text-muted)]
                               hover:text-[var(--color-text)]
                               hover:bg-[var(--color-surface-elevated)]/60
                               transition-colors rounded-full
                               focus:outline-none focus-visible:ring-2
                               focus-visible:ring-[var(--color-primary)]"
                  >
                    <HiShare size={16} />
                  </button>
                )}
                {showSubscribe && onSubscribe && (
                  <button
                    onClick={onSubscribe}
                    aria-label="Subscribe to podcast"
                    className="flex items-center justify-center w-8 h-8
                               text-[var(--color-text-muted)]
                               hover:text-[var(--color-text)]
                               hover:bg-[var(--color-surface-elevated)]/60
                               transition-colors rounded-full
                               focus:outline-none focus-visible:ring-2
                               focus-visible:ring-[var(--color-primary)]"
                  >
                    <HiRss size={16} />
                  </button>
                )}
                {onInfo && (
                  <button
                    onClick={onInfo}
                    aria-label="Episode information"
                    className="flex items-center justify-center w-8 h-8
                               text-[var(--color-text-muted)]
                               hover:text-[var(--color-text)]
                               hover:bg-[var(--color-surface-elevated)]/60
                               transition-colors rounded-full
                               focus:outline-none focus-visible:ring-2
                               focus-visible:ring-[var(--color-primary)]"
                  >
                    <HiInformationCircle size={16} />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Live Mode Controls - just volume */}
          {isLive && (
            <div className="flex items-center justify-center px-4 py-2.5
                            border-t border-[var(--color-border)]/30
                            bg-[var(--color-surface)]/60 backdrop-blur-xl">
              <VolumeControl
                volume={volume}
                muted={muted}
                onVolumeChange={setVolume}
                onMuteToggle={toggleMute}
              />
            </div>
          )}
        </div>
      </div>

      {/* Hidden audio element */}
      <audio ref={audioRef} preload="metadata" />
    </div>
  );
}
